import { maskitoInitialCalibrationPlugin, maskitoTransform } from '@maskito/core'
import type { MaskitoNumberParams } from '@maskito/kit'
import { maskitoNumberOptionsGenerator } from '@maskito/kit'
import { useMaskito } from '@maskito/react'
import { useCallback, useEffect, useRef, useMemo, type CSSProperties } from 'react'

import { TextField } from '@loadsmart/miranda-react'
import type { TextFieldProps, WCTextField } from '@loadsmart/miranda-react'

export type CurrencyMask = MaskitoNumberParams

export const defaultCurrencyMask: Partial<MaskitoNumberParams> = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  thousandSeparator: ',',
  decimalSeparator: '.',
  min: 0,
  prefix: '$',
}

type CurrencyFieldProps = Readonly<{
  onChange?: TextFieldProps['onChange']
  /** Maskito still formats strings; pass a number so the first paint matches the mask (e.g. RHF number fields). */
  value: string | number | null
  disabled?: boolean
  labelledBy: string
  placeholder?: string
  currencyMask?: CurrencyMask
  size?: TextFieldProps['size']
  style?: CSSProperties
  'data-testid'?: string
}>

export function CurrencyField({
  onChange,
  value,
  disabled,
  labelledBy,
  placeholder = '$',
  currencyMask = defaultCurrencyMask,
  size,
  style,
  'data-testid': dataTestId,
}: CurrencyFieldProps) {
  const maskOptions = useMemo(() => getMaskOptions(currencyMask), [currencyMask])

  // Miranda's TextField WC is not a controlled component — it ignores React `value`
  // prop updates after mount. We need a ref to the host element to imperatively
  // sync the shadow input when `displayValue` changes (e.g. cross-field calculations).
  const hostRef = useRef<HTMLElement | null>(null)

  const maskitoRef = useMaskito({
    options: maskOptions,
    elementPredicate: getElementPredicate,
  })

  // Combine hostRef capture with Maskito's ref so both get the same element.
  const setRef = useCallback(
    (el: HTMLElement | null) => {
      hostRef.current = el
      maskitoRef(el)
    },
    [maskitoRef]
  )

  const displayValue = useMemo(() => {
    return maskitoTransform(String(value) || '', maskOptions)
  }, [value, maskOptions])

  // Imperatively write to the shadow input when the value changes programmatically.
  // No event dispatched — avoids triggering onChange and creating an update loop.
  // The guard skips the write when Maskito already applied the same value (user typing).
  useEffect(() => {
    let mounted = true
    const host = hostRef.current
    if (!host) return
    getElementPredicate(host).then(input => {
      if (mounted && input.value !== displayValue) {
        input.value = displayValue
      }
    })
    return () => {
      mounted = false
    }
  }, [displayValue])

  return (
    <TextField
      ref={setRef}
      data-testid={dataTestId ?? 'm-currency-input'}
      disabled={disabled}
      value={displayValue}
      size={size}
      placeholder={placeholder}
      onChange={onChange}
      aria-labelledby={labelledBy}
      style={style}
    />
  )
}

export function getMaskOptions(currencyMask: CurrencyMask) {
  const options = maskitoNumberOptionsGenerator(currencyMask)
  const plugins = [maskitoInitialCalibrationPlugin(options)]
  return {
    ...options,
    plugins,
  }
}

export async function getElementPredicate(host: HTMLElement) {
  const field = host as WCTextField & { updateComplete?: Promise<boolean> }
  await field.updateComplete
  return field.input!
}
